import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../../../shared/lib/apiClient';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const avatarColor = (name) => {
  const colors = [
    'bg-violet-100 text-violet-700',
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-sky-100 text-sky-700',
    'bg-indigo-100 text-indigo-700',
  ];
  const i = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return colors[i % colors.length];
};

const ROLE_CONFIG = {
  owner:  { label: 'Owner',  bg: 'bg-zinc-900',   text: 'text-white'    },
  admin:  { label: 'Admin',  bg: 'bg-zinc-200',   text: 'text-zinc-700' },
  member: { label: 'Member', bg: 'bg-zinc-100',   text: 'text-zinc-500' },
};

function countDescendants(node) {
  if (!node.children?.length) return 0;
  return node.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0);
}

function buildTree(members) {
  const nodeMap = {};
  members.forEach(m => { nodeMap[m.userId] = { ...m, children: [] }; });
  const roots = [];
  members.forEach(m => {
    if (m.reportsTo && nodeMap[m.reportsTo]) {
      nodeMap[m.reportsTo].children.push(nodeMap[m.userId]);
    } else {
      roots.push(nodeMap[m.userId]);
    }
  });
  return roots;
}

function isAncestor(members, nodeId, ancestorId) {
  const memberMap = {};
  members.forEach(m => { memberMap[m.userId] = m; });
  let current = memberMap[nodeId];
  while (current) {
    if (current.reportsTo === ancestorId) return true;
    current = memberMap[current.reportsTo];
  }
  return false;
}

/* ── Card component (used both in-tree and as drag ghost) ───── */

const CardContent = ({ node, isGhost }) => {
  const c = ROLE_CONFIG[node.role] || ROLE_CONFIG.member;
  return (
    <div className={`${isGhost ? 'w-40' : 'w-40'} rounded-xl border bg-white select-none
      ${isGhost ? 'border-zinc-900 shadow-2xl ring-2 ring-zinc-900/10' : 'border-zinc-200 shadow-sm'}
    `}>
      <div className="p-2.5 text-center">
        {node.avatarUrl ? (
          <img src={node.avatarUrl} alt="" className="w-9 h-9 rounded-xl object-cover mx-auto mb-1.5" />
        ) : (
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-semibold mx-auto mb-1.5 ${avatarColor(node.fullName || node.email)}`}>
            {getInitials(node.fullName || node.email?.split('@')[0])}
          </div>
        )}
        <p className="text-[11px] font-medium text-zinc-900 truncate">{node.fullName || node.email?.split('@')[0] || 'Unnamed'}</p>
        {node.jobTitle && <p className="text-[9px] text-zinc-400 truncate mt-0.5">{node.jobTitle}</p>}
        {node.department && <p className="text-[9px] text-zinc-300 truncate">{node.department}</p>}
        <span className={`inline-block mt-1.5 text-[8px] uppercase tracking-widest px-2 py-0.5 rounded-md font-medium ${c.bg} ${c.text}`}>
          {c.label}
        </span>
      </div>
    </div>
  );
};

/* ── Org node ────────────────────────────────────────────────── */

const OrgNode = ({ node, canDrag, dragState, onDragStart, collapsed, onToggleCollapse, nodeRefs, depth = 0 }) => {
  const isBeingDragged = dragState?.draggedId === node.userId;
  const isDescendantOfDragged = dragState?.descendants?.has(node.userId);
  const isDropTarget = dragState?.hoveredId === node.userId;
  const isCollapsed = collapsed?.has(node.userId);
  const hasChildren = node.children?.length > 0;
  const descendantCount = hasChildren ? countDescendants(node) : 0;

  const handleMouseDown = (e) => {
    if (!canDrag || e.button !== 0) return;
    onDragStart(node.userId, e);
  };

  return (
    <div className="flex flex-col items-center">
      <div
        ref={el => { if (nodeRefs) nodeRefs.current[node.userId] = el; }}
        data-node-id={node.userId}
        onMouseDown={handleMouseDown}
        className={`relative transition-all duration-150
          ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isBeingDragged || isDescendantOfDragged ? 'opacity-30 scale-95' : 'opacity-100'}
          ${isDropTarget ? 'ring-2 ring-zinc-900/20 rounded-xl' : ''}
        `}
      >
        <CardContent node={node} />
        {canDrag && (
          <div className="absolute top-2 right-2 text-zinc-300 pointer-events-none">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <circle cx="4" cy="3" r="1" /><circle cx="8" cy="3" r="1" />
              <circle cx="4" cy="6" r="1" /><circle cx="8" cy="6" r="1" />
              <circle cx="4" cy="9" r="1" /><circle cx="8" cy="9" r="1" />
            </svg>
          </div>
        )}
        {isDropTarget && (
          <div className="absolute inset-0 rounded-xl bg-zinc-900/5 pointer-events-none" />
        )}
      </div>

      {hasChildren && (
        <div className="flex flex-col items-center">
          <div className="relative flex flex-col items-center">
            <div className="w-px h-8 bg-zinc-200" />
            <button
              onClick={() => onToggleCollapse(node.userId)}
              className={`absolute bottom-0 translate-y-1/2 z-10 flex items-center justify-center rounded-full border bg-white transition-all
                ${isCollapsed
                  ? 'w-6 h-6 border-zinc-300 text-zinc-500 shadow-sm hover:border-zinc-500'
                  : 'w-4 h-4 border-zinc-200 text-zinc-300 hover:border-zinc-400 hover:text-zinc-500'
                }`}
            >
              {isCollapsed
                ? <span className="text-[9px] font-semibold leading-none">{descendantCount}</span>
                : <svg width="6" height="6" viewBox="0 0 6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 2l2 2 2-2" /></svg>
              }
            </button>
          </div>

          {!isCollapsed && (
            <div className="flex flex-col items-center">
              <div className="w-px h-4 bg-zinc-200" />
              <div className="flex items-start">
                {node.children.map((child, i) => {
                  const isFirst = i === 0;
                  const isLast  = i === node.children.length - 1;
                  const isOnly  = node.children.length === 1;
                  return (
                    <div key={child.userId} className="relative flex flex-col items-center px-4">
                      {!isOnly && (
                        <div
                          className="absolute top-0 h-px bg-zinc-200"
                          style={{ left: isFirst ? '50%' : 0, right: isLast ? '50%' : 0 }}
                        />
                      )}
                      <div className="w-px h-6 bg-zinc-200" />
                      <OrgNode
                        node={child}
                        canDrag={canDrag}
                        dragState={dragState}
                        onDragStart={onDragStart}
                        collapsed={collapsed}
                        onToggleCollapse={onToggleCollapse}
                        nodeRefs={nodeRefs}
                        depth={depth + 1}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Zoom controls ───────────────────────────────────────────── */

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.15;

const ZoomControls = ({ zoom, onZoomIn, onZoomOut, onReset }) => (
  <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-white border border-zinc-200 rounded-lg shadow-sm p-0.5">
    <button onClick={onZoomOut} className="w-7 h-7 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors" title="Zoom out">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 7h8" /></svg>
    </button>
    <button onClick={onReset} className="px-1.5 h-7 flex items-center justify-center rounded-md text-[10px] tabular-nums text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors min-w-[36px]" title="Reset zoom">
      {Math.round(zoom * 100)}%
    </button>
    <button onClick={onZoomIn} className="w-7 h-7 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors" title="Zoom in">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M7 3v8M3 7h8" /></svg>
    </button>
  </div>
);

/* ── Main component ──────────────────────────────────────────── */

const OrgChartView = ({ members, setMembers, canDrag }) => {
  const [collapsed, setCollapsed] = useState(new Set());
  const [dragState, setDragState] = useState(null);
  const [zoom, setZoom] = useState(1);
  const nodeRefs = useRef({});
  const dragRef = useRef(null);
  const viewportRef = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const roots = buildTree(members);

  // Auto-fit zoom for large teams
  useEffect(() => {
    if (members.length > 10) {
      setZoom(Math.max(ZOOM_MIN, Math.min(1, 8 / members.length + 0.4)));
    } else {
      setZoom(1);
    }
  }, [members.length]);

  // Scroll-wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => {
      const delta = e.deltaY > 0 ? -ZOOM_STEP / 2 : ZOOM_STEP / 2;
      return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta));
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleZoomIn = () => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const handleZoomOut = () => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  const handleZoomReset = () => setZoom(1);

  const onToggleCollapse = useCallback((id) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Collect all descendants of a userId
  const getDescendants = useCallback((userId) => {
    const tree = buildTree(members);
    const set = new Set();
    const walk = (nodes) => {
      for (const n of nodes) {
        if (n.userId === userId) {
          const collect = (node) => {
            node.children?.forEach(c => { set.add(c.userId); collect(c); });
          };
          collect(n);
          return true;
        }
        if (n.children?.length && walk(n.children)) return true;
      }
      return false;
    };
    walk(tree);
    return set;
  }, [members]);

  // Find which node the mouse is over
  const findNodeAtPoint = useCallback((clientX, clientY) => {
    const els = document.elementsFromPoint(clientX, clientY);
    for (const el of els) {
      const nodeEl = el.closest('[data-node-id]');
      if (nodeEl) return nodeEl.getAttribute('data-node-id');
    }
    return null;
  }, []);

  const handleDragStart = useCallback((userId, e) => {
    // Record where on the card the user grabbed
    const el = nodeRefs.current[userId];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // getBoundingClientRect returns scaled values, so divide by zoom to get logical offset
    const z = zoomRef.current;
    const offsetX = (e.clientX - rect.left) / z;
    const offsetY = (e.clientY - rect.top) / z;

    dragRef.current = {
      userId,
      offsetX,
      offsetY,
      startX: e.clientX,
      startY: e.clientY,
      activated: false, // only start visual drag after 8px movement
    };

    const descendants = getDescendants(userId);

    const onMouseMove = (me) => {
      const dr = dragRef.current;
      if (!dr) return;

      // Activation threshold
      if (!dr.activated) {
        const dist = Math.hypot(me.clientX - dr.startX, me.clientY - dr.startY);
        if (dist < 8) return;
        dr.activated = true;
      }

      const hoveredId = findNodeAtPoint(me.clientX, me.clientY);

      setDragState({
        draggedId: dr.userId,
        descendants,
        hoveredId: hoveredId !== dr.userId ? hoveredId : null,
        // Ghost position: cursor minus logical offset (ghost is scaled to match zoom)
        ghostX: me.clientX - dr.offsetX * zoomRef.current,
        ghostY: me.clientY - dr.offsetY * zoomRef.current,
        ghostZoom: zoomRef.current,
      });
    };

    const onMouseUp = (me) => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      const dr = dragRef.current;
      dragRef.current = null;

      if (!dr?.activated) {
        setDragState(null);
        return;
      }

      const targetId = findNodeAtPoint(me.clientX, me.clientY);
      setDragState(null);

      // Determine drop target
      const dropTarget = (!targetId || targetId === dr.userId) ? null : targetId;

      // If dropped on empty space (null) → remove manager (set reportsTo to null)
      // If dropped on a node → set as new manager
      const dragged = members.find(m => m.userId === dr.userId);
      if (!dragged) return;

      // No-op if same parent
      if (dropTarget === null && !dragged.reportsTo) return;
      if (dropTarget === dragged.reportsTo) return;
      // Prevent circular
      if (dropTarget && isAncestor(members, dropTarget, dr.userId)) return;
      // Prevent self
      if (dropTarget === dr.userId) return;

      const prev = members;
      setMembers(members.map(m =>
        m.userId === dr.userId ? { ...m, reportsTo: dropTarget } : m
      ));

      if (String(dr.userId).startsWith('synth_')) return;

      (async () => {
        try {
          const res = await apiFetch(`/api/companies/members/${dr.userId}`, {
            method: 'PATCH',
            body: JSON.stringify({ reports_to: dropTarget }),
          });
          if (!res.ok) setMembers(prev);
        } catch {
          setMembers(prev);
        }
      })();
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [members, setMembers, getDescendants, findNodeAtPoint]);

  if (members.length === 0) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-300 text-sm">
        No members to display
      </div>
    );
  }

  // Find dragged node for ghost
  const findNode = (nodes, id) => {
    for (const n of nodes) {
      if (n.userId === id) return n;
      if (n.children?.length) {
        const found = findNode(n.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  const draggedNode = dragState?.draggedId ? findNode(roots, dragState.draggedId) : null;

  return (
    <div ref={viewportRef} className="relative overflow-hidden rounded-xl min-h-[200px]">
      <ZoomControls
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleZoomReset}
      />

      <div
        className="overflow-auto min-h-[400px] pb-8"
      >
        <div
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          <div className="flex gap-8 justify-center items-start pt-4 pb-10 min-w-max mx-auto">
            {roots.map(root => (
              <OrgNode
                key={root.userId}
                node={root}
                canDrag={canDrag}
                dragState={dragState}
                onDragStart={handleDragStart}
                collapsed={collapsed}
                onToggleCollapse={onToggleCollapse}
                nodeRefs={nodeRefs}
                depth={0}
              />
            ))}
          </div>
        </div>
      </div>

      {dragState && (
        <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest transition-colors duration-150 whitespace-nowrap
          ${!dragState.hoveredId ? 'text-zinc-500' : 'text-zinc-300'}
        `}>
          Drop on empty space to remove manager
        </div>
      )}

      {/* Drag ghost — portaled to body, scaled to match zoom */}
      {draggedNode && dragState && createPortal(
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            transform: `translate(${dragState.ghostX}px, ${dragState.ghostY}px) scale(${dragState.ghostZoom})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
            zIndex: 99999,
            opacity: 0.92,
          }}
        >
          <CardContent node={draggedNode} isGhost />
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrgChartView;
